'use client';

import { cn } from '@/lib/utils';
import { formatFileSize } from '@/utils/format-file-size';
import { useCallback } from 'react';
import { Accept, FileRejection, useDropzone } from 'react-dropzone';

export interface MediaDropzoneProps {
  onDrop: (files: File[]) => void;
  onDropRejected?: (rejections: FileRejection[]) => void;
  acceptedTypes: Accept;
  maxSize: number;
  multiple?: boolean;
  className?: string;
  children?: React.ReactNode | ((props: { open: () => void }) => React.ReactNode);
}

export function MediaDropzone({
  onDrop,
  onDropRejected,
  acceptedTypes,
  maxSize,
  multiple = false,
  className,
  children
}: MediaDropzoneProps) {
  const handleDrop = useCallback((acceptedFiles: File[]) => {
    if (!acceptedFiles || acceptedFiles.length === 0) {
      console.warn('No valid files were dropped');
      return;
    }
    onDrop(acceptedFiles);
  }, [onDrop]);

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop: handleDrop,
    onDropRejected,
    accept: acceptedTypes,
    maxSize,
    multiple,
    noClick: true
  });

  return (
    <div
      {...getRootProps()}
      className={cn(
        isDragActive
          ? 'border-primary-500 bg-primary-50'
          : 'border-gray-300 hover:border-gray-400',
        className
      )}
    >
      <input {...getInputProps()} />

      {typeof children === 'function' ? children({ open }) : children || (
        <div className="text-center">
          <div className="my-4">
            <p className="text-sm text-gray-600">
              Drag and drop your {multiple ? 'files' : 'file'} here, or{' '}
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  open();
                }}
                className="text-primary-600 hover:text-primary-500 cursor-pointer"
              >
                browse
              </span>
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {Object.keys(acceptedTypes).join(', ')} up to {formatFileSize(maxSize)}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
